// ############################################################
// Copyright (C) 2021-2025 by Gamify Software Inc - All Rights Reserved
// You may NOT distribute or modify this code without the expressed
// written consent of Gamify Software Inc.
// #############################################################

import React, { useState, useMemo } from 'react';
import { Box, Tabs, Tab } from '@mui/material';
import featureFlag from '../../common/featureFlag';
import { useTheme } from '../../context/PermissionsContext/ThemeContext/ThemeContext';
import DealPhotos from '../../routes/deals/selectedDeal/DealPhotos';
import DealNotes from '../../routes/deal/components/DealNotes';
import DealLeadInfo from '../../routes/deal/components/DealLeadInfo';
import DealDoorData from '../../routes/deal/components/DealDoorData';
import ClaimTab from '../ClaimTab/ClaimTab';
import DealContracts from '../../routes/deal/components/DealContracts';
import DealStatus from '../../routes/deal/components/DealStatus';
import DealAppointments from '../../routes/deal/components/DealAppointments';
import NotesToHankChat from '../NotesToHankChat/NotesToHankChat';

// ── Tab order config (mirrors mobile tabOrderConfig.ts) ─────

const DEFAULT_TAB_ORDER = {
  aiInspection: [
    'AI',
    'Photos',
    'Project',
    'Contracts',
    'Notes',
    'Lead',
    'Door data',
    'Appointments',
    'Reviews',
  ],
  standard: [
    'Door data',
    'Notes',
    'Status',
    'Appointments',
    'Lead',
    'Contracts',
    'Photos',
    'Project',
    'Reviews',
  ],
};

type TabOrderMode = 'aiInspection' | 'standard';

function getTabOrder(mode: TabOrderMode): string[] {
  const orgConfig = (global as any)?.appSettings?.tabOrder;
  if (orgConfig?.[mode]?.length) {
    // Orders saved before mobile renamed Claim -> Project still say 'Claim'
    return orgConfig[mode].map((label: string) =>
      label === 'Claim' ? 'Project' : label
    );
  }
  return DEFAULT_TAB_ORDER[mode];
}

function sortTabsByOrder(order: string[], visible: string[]): string[] {
  const sorted: string[] = [];
  const remaining = [...visible];

  for (const label of order) {
    const idx = remaining.indexOf(label);
    if (idx !== -1) {
      sorted.push(label);
      remaining.splice(idx, 1);
    }
  }
  sorted.push(...remaining);
  return sorted;
}

// ── Types ───────────────────────────────────────────────────

interface TabDef {
  label: string;
  content: React.ReactNode;
}

export interface DealTabbedContentProps {
  deal: any;
  dealId: string;
  comments?: any[];
  categoryId?: string;
  appointments?: any[];
  isOnKanban?: boolean;
  showEdit?: boolean;
  isReadOnly?: boolean;
  isImageUploading?: boolean;
  setIsImageUploading?: (val: boolean) => void;
  onTriggerSelectedDealRefetch?: () => void;
  handleRefetchDeals?: (...args: any[]) => void;
  handleImageClick?: (index: number) => void;
  onImagesChange?: (photos: any[]) => void;
  onStagePress?: () => void;
  pollTick?: number;
}

// ── Tab builder (mirrors mobile buildTabs logic) ────────────

function buildTabs(props: DealTabbedContentProps): TabDef[] {
  const {
    deal,
    dealId,
    comments = [],
    categoryId,
    appointments = [],
    isOnKanban = false,
    showEdit = false,
    isReadOnly = false,
    isImageUploading = false,
    setIsImageUploading,
    onTriggerSelectedDealRefetch,
    handleRefetchDeals,
    handleImageClick,
    onImagesChange,
    onStagePress,
    pollTick,
  } = props;

  const tabs: TabDef[] = [];
  const isAI = featureFlag('AIInspection');

  // Photos tab
  tabs.push({
    label: 'Photos',
    content: (
      <DealPhotos
        photos={Array.isArray(deal?.files?.items) ? deal.files.items : []}
        deal={deal}
        isImageUploading={isImageUploading}
        setIsImageUploading={setIsImageUploading || (() => {})}
        onTriggerSelectedDealRefetch={
          onTriggerSelectedDealRefetch || (() => {})
        }
        handleRefetchDeals={handleRefetchDeals || (() => {})}
        handleImageClick={handleImageClick || (() => {})}
        isOnKanban={isOnKanban}
        showEdit={showEdit && !isReadOnly}
        onImagesChange={onImagesChange}
      />
    ),
  });

  // Project tab (the unified claim tab; same label as mobile). Visible for all
  // accounts — contains Reports, Estimates, Measurement Reports, Claims, and
  // Inspection Q&A inline, matching mobile
  // NOTE: isReadOnly is intentionally NOT passed to ClaimTab. The /deal/ route
  // sets isReadOnly for deal metadata (name, contacts, stage) but the Claims
  // workflow should remain fully functional for external users like estimators
  // (Azar) who access deals via /deal/:dealId. This is their primary workflow.
  tabs.push({
    label: 'Project',
    content: dealId ? (
      <ClaimTab dealID={dealId} dealInfo={deal} pollTick={pollTick} />
    ) : null,
  });

  // Contracts tab
  if (
    (global as any)?.appSettings?.features?.deal?.signOnDevice ||
    (global as any)?.appSettings?.features?.deal?.signUsingEmail
  ) {
    tabs.push({
      label: 'Contracts',
      content: deal ? <DealContracts deal={deal} /> : null,
    });
  }

  // Notes tab
  tabs.push({
    label: 'Notes',
    content: dealId ? (
      <DealNotes
        dealId={dealId}
        comments={comments}
        categoryId={categoryId}
        currentOwnerID={deal?.currentOwnerID}
        onRefetch={onTriggerSelectedDealRefetch || (() => {})}
        showEdit={showEdit && !isReadOnly}
      />
    ) : null,
  });

  // Lead tab
  tabs.push({
    label: 'Lead',
    content: <DealLeadInfo dealInfo={deal} />,
  });

  // Door data tab
  tabs.push({
    label: 'Door data',
    content: <DealDoorData dealInfo={deal} />,
  });

  // Status tab (standard mode only)
  if (!isAI) {
    tabs.push({
      label: 'Status',
      content: <DealStatus dealInfo={deal} onStagePress={onStagePress} />,
    });
  }

  // Appointments tab
  if ((global as any)?.appSettings?.features?.schedule?.enabled) {
    tabs.push({
      label: 'Appointments',
      content: <DealAppointments appointments={appointments} />,
    });
  }

  // Reviews tab (placeholder, same as mobile)
  if (featureFlag('reviewRequest')) {
    tabs.push({
      label: 'Reviews',
      content: (
        <Box
          sx={{ padding: '20px', textAlign: 'center', color: 'text.secondary' }}
        >
          Review display coming soon
        </Box>
      ),
    });
  }

  // AI tab (Notes to Hank chat)
  if (featureFlag('notesToHank')) {
    tabs.push({
      label: 'AI',
      content: dealId ? (
        <NotesToHankChat dealId={dealId} dealInfo={deal} />
      ) : null,
    });
  }

  // Sort by org tab order config
  const mode: TabOrderMode = isAI ? 'aiInspection' : 'standard';
  const order = getTabOrder(mode);
  const visibleLabels = tabs.map((t) => t.label);
  const sortedLabels = sortTabsByOrder(order, visibleLabels);

  const sorted: TabDef[] = [];
  const remaining = [...tabs];
  for (const label of sortedLabels) {
    const idx = remaining.findIndex((t) => t.label === label);
    if (idx !== -1) {
      sorted.push(remaining[idx]);
      remaining.splice(idx, 1);
    }
  }
  return sorted;
}

// ── Component ───────────────────────────────────────────────

const DealTabbedContent: React.FC<DealTabbedContentProps> = (props) => {
  const { theme } = useTheme();
  const [activeTab, setActiveTab] = useState(0);

  const tabs = useMemo(
    () => buildTabs(props),
    [
      props.deal,
      props.dealId,
      props.comments,
      props.appointments,
      props.showEdit,
      props.isReadOnly,
      props.isImageUploading,
      props.pollTick,
    ]
  );

  // Clamp activeTab if tabs change and current index is out of bounds
  const safeTab = activeTab >= tabs.length ? 0 : activeTab;

  return (
    <Box>
      <Box sx={{ borderBottom: 1, borderColor: theme.stroke, width: '100%' }}>
        <Tabs
          value={safeTab}
          onChange={(_e, newValue) => setActiveTab(newValue)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            '& .MuiTab-root': {
              color: theme.textLight,
              fontWeight: 500,
              textTransform: 'none',
              fontSize: 14,
              minHeight: 40,
              padding: '8px 16px',
            },
            '& .Mui-selected': {
              color: `${theme.primary} !important`,
              fontWeight: 600,
            },
            '& .MuiTabs-indicator': {
              backgroundColor: theme.primary,
            },
          }}
        >
          {tabs.map((tab, idx) => (
            <Tab key={idx} label={tab.label} />
          ))}
        </Tabs>
      </Box>

      <Box sx={{ mt: 2 }}>{tabs[safeTab]?.content}</Box>
    </Box>
  );
};

export default DealTabbedContent;
export { buildTabs };
export type { TabDef };
